import { Link } from "react-router-dom";
import { Check, ChevronRight } from "lucide-react";
import { formatBRL } from "../../features/plans/lib/format";
import type { LandingPlan } from "../../features/plans/lib/landingPlanTypes";
import { track } from "./landingTrack";

/** Fixed installment line. The card never shows an installment count. */
export const LANDING_INSTALLMENT_LINE = "Parcelamento: veja no checkout";

/**
 * One live catalog card.
 *
 * Product strings come from `plan` only: title, description, price,
 * periodLabel, features, and the popular flag. `formatBRL` formats the
 * numeric catalog price. An empty description or feature list renders no
 * slot. `featureRemainder` is how many features were not listed. `plan.href`
 * is the details destination. There is no exam-cycle label and no question
 * count. This component does not read the static catalog.
 */
export function LandingPlanCard({ plan }: { plan: LandingPlan }) {
  const featured = plan.popular;
  const className = featured
    ? "landing-plans__card landing-plans__card--featured"
    : "landing-plans__card";
  const remainder =
    plan.featureRemainder > 0
      ? `+${plan.featureRemainder} benefícios em Ver detalhes`
      : "";

  return (
    <article
      className={className}
      data-featured={featured ? "true" : undefined}
    >
      {featured ? <p className="landing-plans__badge">Mais popular</p> : null}
      <h3 className="landing-plans__title">{plan.title}</h3>
      {plan.description !== "" ? (
        <p className="landing-plans__description">{plan.description}</p>
      ) : null}
      {plan.features.length > 0 ? (
        <ul className="landing-plans__features">
          {plan.features.map((feature, index) => (
            <li key={`${plan.id}-${index}`} className="landing-plans__feature">
              <Check size={16} aria-hidden="true" />
              <span>{feature}</span>
            </li>
          ))}
        </ul>
      ) : null}
      {remainder !== "" ? (
        <p className="landing-plans__remainder">{remainder}</p>
      ) : null}
      <p className="landing-plans__price-block">
        <span className="landing-plans__price">{formatBRL(plan.price)}</span>
        {plan.periodLabel !== "" ? (
          <span className="landing-plans__period">{plan.periodLabel}</span>
        ) : null}
        <span className="landing-plans__installment">{LANDING_INSTALLMENT_LINE}</span>
      </p>
      <div className="landing-plans__rule" aria-hidden="true" />
      <Link
        className="landing-plans__details"
        to={plan.href}
        onClick={() => {
          track("landing_plan_clicked", { product_id: plan.id });
        }}
      >
        <span>Ver detalhes</span>
        <ChevronRight size={18} aria-hidden="true" />
      </Link>
    </article>
  );
}
